import { useApolloClient, useMutation } from '@apollo/client/react';
import { useCallback, useState, type ReactNode } from 'react';

import {
  API_KEYS,
  CREATE_API_KEY,
  ping,
  pingUrl,
  REGENERATE_API_KEY,
  REVOKE_API_KEY,
  type ApiKey,
  type PingResult,
} from '@/account/api-keys';
import { errorCode } from '@/account/arcade-api';
import { Button } from '@/components/Button/Button';
import { CreateApiKeyForm } from '@/components/CreateApiKeyForm/CreateApiKeyForm';
import { Dialog } from '@/components/Dialog/Dialog';
import { SecretReveal } from '@/components/SecretReveal/SecretReveal';
import { useToast } from '@/toast/useToast';

type Open =
  | { kind: 'create' }
  | { kind: 'regenerate'; key: ApiKey }
  | { kind: 'revoke'; key: ApiKey }
  | { kind: 'reveal'; key: ApiKey; secret: string; ping: PingResult | 'pending' | null };

export interface ApiKeyActions {
  openCreate: () => void;
  openRegenerate: (key: ApiKey) => void;
  openRevoke: (key: ApiKey) => void;
  /** True once Arcade refused a new key for the limit, until one is revoked. */
  limitReached: boolean;
  dialog: ReactNode;
}

const LIMIT_MESSAGE =
  'You already have the most active keys allowed. Revoke one to create another.';

function nameOf(key: ApiKey): string {
  return key.name || 'Untitled key';
}

/** Create, regenerate and revoke, and the dialogs each one needs. */
export function useApiKeyActions(): ApiKeyActions {
  const client = useApolloClient();
  const toast = useToast();
  const [open, setOpen] = useState<Open | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [limitReached, setLimitReached] = useState(false);
  const [createKey, created] = useMutation(CREATE_API_KEY, { refetchQueries: [API_KEYS] });
  const [regenerateKey, regenerated] = useMutation(REGENERATE_API_KEY);
  const [revokeKey, revoked] = useMutation(REVOKE_API_KEY);

  const close = useCallback(() => {
    setOpen(null);
    setError(null);
  }, []);

  const onCreate = useCallback(
    async (name: string) => {
      setError(null);
      try {
        const { data } = await createKey({ variables: { name } });
        if (data) {
          const { key, secret } = data.createApiKey;
          setOpen({ kind: 'reveal', key, secret, ping: null });
        }
      } catch (failure) {
        if (errorCode(failure) === 'TOO_MANY_KEYS') {
          setLimitReached(true);
          setError(LIMIT_MESSAGE);
        } else {
          setError('Could not create the key. Try again.');
        }
      }
    },
    [createKey],
  );

  const onRegenerate = useCallback(
    async (key: ApiKey) => {
      try {
        const { data } = await regenerateKey({ variables: { id: key.id } });
        if (data) {
          const { key: updated, secret } = data.regenerateApiKey;
          setOpen({ kind: 'reveal', key: updated, secret, ping: null });
        }
      } catch (failure) {
        close();
        toast.error({
          title: 'Could not regenerate the key',
          description:
            errorCode(failure) === 'KEY_REVOKED'
              ? 'A revoked key cannot be regenerated.'
              : 'Try again in a moment.',
        });
      }
    },
    [regenerateKey, toast, close],
  );

  const onRevoke = useCallback(
    async (key: ApiKey) => {
      try {
        await revokeKey({ variables: { id: key.id } });
        setLimitReached(false);
      } catch {
        toast.error({ title: 'Could not revoke the key', description: 'Try again in a moment.' });
      }
      close();
    },
    [revokeKey, toast, close],
  );

  const onPing = useCallback(
    async (secret: string) => {
      setOpen((current) =>
        current?.kind === 'reveal' ? { ...current, ping: 'pending' } : current,
      );
      const result = await ping(secret);
      setOpen((current) => (current?.kind === 'reveal' ? { ...current, ping: result } : current));
      void client.refetchQueries({ include: 'active' });
    },
    [client],
  );

  let dialog: ReactNode = null;
  if (open?.kind === 'create') {
    dialog = (
      <Dialog
        title="Create an API Key"
        size="wide"
        description="The secret is shown once, right after the key is created."
        onClose={close}
        dismissable={!created.loading}
      >
        <CreateApiKeyForm
          pending={created.loading}
          error={error}
          onSubmit={onCreate}
          onCancel={close}
        />
      </Dialog>
    );
  } else if (open?.kind === 'regenerate') {
    dialog = (
      <Dialog
        title={`Regenerate “${nameOf(open.key)}”?`}
        description="A new secret is issued for this key. The current secret stops working immediately, so anything using it will need the new one."
        onClose={close}
        dismissable={!regenerated.loading}
        actions={
          <>
            <Button variant="ghost" onClick={close} disabled={regenerated.loading}>
              Cancel
            </Button>
            <Button loading={regenerated.loading} onClick={() => onRegenerate(open.key)}>
              Regenerate secret
            </Button>
          </>
        }
      />
    );
  } else if (open?.kind === 'revoke') {
    dialog = (
      <Dialog
        title={`Revoke “${nameOf(open.key)}”?`}
        description="Requests made with this key will be refused from now on. This cannot be undone; its history stays visible."
        onClose={close}
        dismissable={!revoked.loading}
        actions={
          <>
            <Button variant="ghost" onClick={close} disabled={revoked.loading}>
              Cancel
            </Button>
            <Button variant="danger" loading={revoked.loading} onClick={() => onRevoke(open.key)}>
              Revoke key
            </Button>
          </>
        }
      />
    );
  } else if (open?.kind === 'reveal') {
    dialog = (
      <Dialog
        title={`Your key “${nameOf(open.key)}”`}
        onClose={close}
        dismissable={false}
        size="wide"
        actions={<Button onClick={close}>I’ve saved my key</Button>}
      >
        <SecretReveal
          secret={open.secret}
          pingUrl={pingUrl()}
          ping={open.ping}
          onPing={() => onPing(open.secret)}
        />
      </Dialog>
    );
  }

  return {
    openCreate: () => setOpen({ kind: 'create' }),
    openRegenerate: (key) => setOpen({ kind: 'regenerate', key }),
    openRevoke: (key) => setOpen({ kind: 'revoke', key }),
    limitReached,
    dialog,
  };
}
